import { Badge } from '@/components/ui/badge';
import { TimezoneTooltip } from './TimezoneTooltip';
import { cn } from '@/lib/utils';

interface DateDisplayProps {
  /**
   * The formatted date/time string to display
   */
  children: React.ReactNode;
  /**
   * The UTC date value (for timezone conversion tooltip)
   */
  utcDate: Date | string | number;
  /**
   * Whether this is a datetime (show UTC badge) or date-only (no badge)
   * Default: true (show UTC badge)
   */
  showUtcBadge?: boolean;
  /**
   * Whether to show the timezone tooltip
   * Default: true
   */
  showTimezoneTooltip?: boolean;
  /**
   * Optional className for the container
   */
  className?: string;
  /**
   * Whether this is date-only (no time component)
   * Default: false
   */
  dateOnly?: boolean;
}

/**
 * DateDisplay wraps a formatted date/time display with:
 * - Optional UTC badge (for datetime displays)
 * - Timezone conversion tooltip
 * - Consistent styling
 */
export function DateDisplay({
  children,
  utcDate,
  showUtcBadge = true,
  showTimezoneTooltip = true,
  dateOnly = false,
  className,
}: DateDisplayProps) {
  // Only show UTC badge if it's a datetime (not date-only) and showUtcBadge is true
  const shouldShowBadge = showUtcBadge && !dateOnly;

  return (
    <span className={cn('inline-flex items-center gap-1', className)}>
      <span>{children}</span>
      {shouldShowBadge && (
        <Badge variant="outline" className="text-xs font-normal">
          UTC
        </Badge>
      )}
      {showTimezoneTooltip && <TimezoneTooltip utcDate={utcDate} dateOnly={dateOnly} />}
    </span>
  );
}
